import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import classNames from 'classnames';
import type { AiUserAgent } from 'components/framework/models';
import type { RecordId } from 'javascripts/shared-types';
import styles from './AiTemplatesModal.module.css';
import { agentTypeNames } from './AiTemplateController';

interface SortableFavoriteRowProps {
  template: AiUserAgent;
  index: number;
  favoriteId: RecordId;
  moveRow: (dragId: RecordId, hoverIndex: number) => void;
  onToggleFavorite: (template: AiUserAgent) => void;
  onPlaceTemplate: (template: AiUserAgent) => void;
}

const FAVORITE_ROW_TYPE = 'FAVORITE_ROW';

interface DragItem {
  favoriteId: RecordId;
  type: string;
}

const SortableFavoriteRow: React.FC<SortableFavoriteRowProps> = ({
  template,
  index,
  favoriteId,
  moveRow,
  onToggleFavorite,
  onPlaceTemplate,
}) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLTableRowElement>(null);

  const [{ isOver, canDrop }, drop] = useDrop<
    DragItem,
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: FAVORITE_ROW_TYPE,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop(item, monitor) {
      if (!previewRef.current) {
        return;
      }
      const dragId = item.favoriteId;
      const dropIndex = index;

      if (parseInt(dragId) === dropIndex) {
        return;
      }

      moveRow(dragId, dropIndex);
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    DragItem,
    void,
    { isDragging: boolean }
  >({
    item: { favoriteId, type: FAVORITE_ROW_TYPE },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(dragRef);
  preview(drop(previewRef));

  return (
    <tr
      ref={previewRef}
      style={{
        opacity: isDragging ? 0.3 : 1,
        transform: isDragging ? 'scale(0.95)' : 'scale(1)',
        transition: 'all 0.2s ease',
        backgroundColor:
          isOver && canDrop
            ? 'var(--theme-primary-background-light)'
            : undefined,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      className={classNames(styles.favoriteRow, {
        [styles.dragging]: isDragging,
        [styles.dragOver]: isOver && canDrop,
      })}
    >
      <td>
        <div className='tw-flex tw-items-center'>
          <div
            ref={dragRef}
            className={classNames(styles.dragHandle, 'tw-cursor-move')}
            style={{
              transform: isDragging ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.2s ease',
            }}
          >
            <i className='fa fa-grip-vertical' />
          </div>
          <aha-icon
            icon='fa fa-star'
            style={{
              color: 'var(--aha-yellow-400)',
              marginRight: '8px',
              marginLeft: '8px',
              cursor: 'pointer',
            }}
            onClick={event => {
              event.stopPropagation();
              onToggleFavorite(template);
            }}
            class={styles.favoriteIcon}
          />
          <span
            onClick={() => {
              onPlaceTemplate(template);
            }}
            className='tw-cursor-pointer'
          >
            {template.name}
          </span>
        </div>
      </td>
      <td>
        <div className={styles.templateTileTag}>
          {agentTypeNames[template.agentType]}
        </div>
      </td>
    </tr>
  );
};

export default SortableFavoriteRow;
